import { api } from "./client";

export type Project = {
  id: number;
  name: string;
  description: string;
  width: number;
  height: number;
  fps: number;
  duration_ms: number;
  timeline_json?: Timeline;
  created_at: string;
  updated_at: string;
};

export type Timeline = {
  version: number;
  fps: number;
  width: number;
  height: number;
  background: string;
  tracks: Track[];
  overlays: Overlay[];
};

export type Track =
  | { id: string; type: "video"; name?: string; muted?: boolean; solo?: boolean; clips: ClipRef[] }
  | { id: string; type: "audio"; name?: string; muted?: boolean; solo?: boolean; ducked?: boolean;
      clips: ClipRef[]; volume: number };

export type ClipRef = {
  id: string;            // id local del timeline (uuid corto)
  clipId: number;        // FK a MediaClip
  in: number;            // ms
  out: number;           // ms
  start: number;         // ms en timeline
  volume?: number;       // 0..2, sólo aplica a clips de audio
  effects?: Effect[];
  transitionOut?: { type: string; duration_ms: number };
};

export type EffectBase = { id: string; enabled?: boolean };

export type Effect = EffectBase & (
  | { type: "lut"; file: string; intensity?: number }
  | { type: "adjust"; exposure?: number; contrast?: number; saturation?: number; temperature?: number }
  | { type: "blur"; radius?: number }
);

export type TextAnimation =
  | "none" | "fade" | "slide-left" | "slide-right" | "slide-up" | "slide-down"
  | "zoom" | "pop" | "typewriter";

export type TextOverlay = {
  id: string;
  type: "text";
  text: string;
  start: number;          // ms en timeline
  end: number;            // ms en timeline
  x: number;              // 0..1 percentual del ancho del lienzo
  y: number;              // 0..1 percentual del alto del lienzo
  font_size: number;      // px relativos a un lienzo de 1080 alto
  color: string;          // hex
  bold?: boolean;
  align?: "left" | "center" | "right";
  background?: string | null;  // null = sin caja
  padding?: number;       // px (sobre 1080 alto)
  fade_in_ms?: number;
  fade_out_ms?: number;
  animation_in?: TextAnimation;
  animation_out?: TextAnimation;
};

export type Overlay = TextOverlay;

export async function listProjects(): Promise<Project[]> {
  const r = await api.get("/projects/");
  return r.data.results || r.data;
}

export async function createProject(name: string): Promise<Project> {
  const r = await api.post("/projects/", { name, width: 1920, height: 1080, fps: 30 });
  return r.data;
}

export async function getProject(id: number): Promise<Project> {
  const r = await api.get(`/projects/${id}/`);
  return r.data;
}

export async function saveTimeline(id: number, timeline_json: Timeline, duration_ms: number) {
  const r = await api.patch(`/projects/${id}/timeline/`, { timeline_json, duration_ms });
  return r.data as Project;
}

export async function deleteProject(id: number) {
  await api.delete(`/projects/${id}/`);
}
